import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { readStoredAuth } from '@core/auth/tokenStorage';
import type { User } from '@/types/user';

export interface AuthState {
  token: string | null;
  user: User | null;
  isAuthenticated: boolean;
  /** Set by the session middleware on a 401. The shell reads it, not each query. */
  sessionExpired: boolean;
}

const seed = readStoredAuth();

const initialState: AuthState = {
  token: seed.token,
  user: seed.user,
  isAuthenticated: Boolean(seed.token),
  sessionExpired: false
};

/**
 * The slice name is load-bearing: `baseQuery.prepareHeaders` reads `state.auth.token`.
 * Rename it and every request silently goes out unauthenticated.
 */
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    login: (state, action: PayloadAction<{ token: string; user: User }>) => {
      state.token = action.payload.token;
      state.user = action.payload.user;
      state.isAuthenticated = true;
      state.sessionExpired = false;
    },
    logout: (state) => {
      state.token = null;
      state.user = null;
      state.isAuthenticated = false;
      state.sessionExpired = false;
    },
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
    },
    sessionExpired: (state) => {
      state.sessionExpired = true;
    },
    clearSessionExpired: (state) => {
      state.sessionExpired = false;
    }
  }
});

export const { login, logout, setUser, sessionExpired, clearSessionExpired } = authSlice.actions;
export const authReducer = authSlice.reducer;
